/* =========================================================================
   BERLINDE storefront behaviour
   Cart drawer, mobile nav, sticky header, variant picker, filters.

   Rendering rule: anything that shows money or product data is re-rendered
   by Liquid through the Section Rendering API, never assembled as a string
   in JS. That keeps currency formatting correct in every market and leaves
   no path for unescaped product text to reach innerHTML.
   ========================================================================= */
(function () {
  'use strict';

  var CART_SECTION = 'berlinde-cart';

  function $(sel, scope) { return (scope || document).querySelector(sel); }
  function $$(sel, scope) { return Array.prototype.slice.call((scope || document).querySelectorAll(sel)); }

  function on(type, selector, handler) {
    document.addEventListener(type, function (event) {
      var el = event.target instanceof Element ? event.target.closest(selector) : null;
      if (el) handler(event, el);
    });
  }

  /* --- Scrolling ---------------------------------------------------------
     Horizon scrolls an inner .page-wrapper; html/body are overflow:hidden and
     the window never fires scroll. Which element is the real scroller depends
     on content height, and that is not settled at DOMContentLoaded, so resolve
     it on every read and listen to both candidates rather than picking once.
     ---------------------------------------------------------------------- */

  function scroller() {
    var wrapper = $('.page-wrapper');
    if (wrapper && wrapper.scrollHeight > wrapper.clientHeight + 1) return wrapper;
    return null;
  }

  function scrollTop() {
    var el = scroller();
    if (el) return el.scrollTop;
    return window.scrollY || document.documentElement.scrollTop || 0;
  }

  function onScroll(handler) {
    var wrapper = $('.page-wrapper');
    if (wrapper) wrapper.addEventListener('scroll', handler, { passive: true });
    window.addEventListener('scroll', handler, { passive: true });
  }

  /* --- Scroll locking ----------------------------------------------------- */

  var locks = 0;
  var lockedEl = null;
  var lockedPrev = '';

  function lockScroll(lock) {
    if (lock) {
      locks += 1;
      if (locks > 1) return;
      lockedEl = scroller() || document.documentElement;
      lockedPrev = lockedEl.style.overflow;
      lockedEl.style.overflow = 'hidden';
    } else {
      locks = Math.max(0, locks - 1);
      if (locks > 0 || !lockedEl) return;
      lockedEl.style.overflow = lockedPrev;
      lockedEl = null;
    }
  }

  // The theme editor re-renders sections underneath us; a half-open drawer must
  // not leave the page permanently unscrollable.
  function releaseAllLocks() {
    if (lockedEl) lockedEl.style.overflow = lockedPrev;
    lockedEl = null;
    locks = 0;
  }

  /* --- Cart drawer -------------------------------------------------------- */

  function isCartOpen() {
    var cart = $('[data-bl-cart]');
    return !!cart && cart.dataset.open === 'true';
  }

  function isNavOpen() {
    var nav = $('[data-bl-nav]');
    return !!nav && nav.dataset.open === 'true';
  }

  function showScrim(show) {
    var scrim = $('[data-bl-scrim]');
    if (scrim) scrim.dataset.open = show ? 'true' : 'false';
  }

  function openCart() {
    var cart = $('[data-bl-cart]');
    if (!cart || cart.dataset.open === 'true') return;
    cart.dataset.open = 'true';
    cart.setAttribute('aria-hidden', 'false');
    showScrim(true);
    lockScroll(true);
    var close = cart.querySelector('[data-bl-cart-close]');
    if (close) close.focus();
  }

  function closeCart() {
    var cart = $('[data-bl-cart]');
    if (!cart || cart.dataset.open !== 'true') return;
    cart.dataset.open = 'false';
    cart.setAttribute('aria-hidden', 'true');
    if (!isNavOpen()) showScrim(false);
    lockScroll(false);
    // Hand focus back to what opened it, or it lands on <body>.
    var trigger = $('[data-bl-cart-open]');
    if (trigger) trigger.focus();
  }

  /* --- Cart requests ------------------------------------------------------
     Line numbers shift the moment a line is removed, so a second request fired
     against the markup of the first would edit the wrong product. One request
     at a time, and the drawer is re-read from the server between them.
     ---------------------------------------------------------------------- */

  var cartBusy = false;

  function setBusy(busy) {
    cartBusy = busy;
    var cart = $('[data-bl-cart]');
    if (cart) cart.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  function cartSectionId() {
    var host = $('[data-bl-cart-host]');
    return (host && host.getAttribute('data-bl-cart-section-id')) || CART_SECTION;
  }

  function sectionsUrl(sectionId) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root + '?sections=' + encodeURIComponent(sectionId);
  }

  function preserveAndSyncUpsell(currentHost, freshHost) {
    var currentUpsell = $('.bl-cart-upsell', currentHost);
    var freshUpsell = $('.bl-cart-upsell', freshHost);

    // A section response can omit product_list settings. Keep the last complete
    // server-rendered upsell instead of losing it during the cart redraw.
    if (!freshUpsell && currentUpsell) {
      var freshBody = $('.bl-cart__body', freshHost);
      if (freshBody) {
        freshUpsell = currentUpsell.cloneNode(true);
        freshBody.appendChild(freshUpsell);
      }
    }

    if (!freshUpsell) return;

    var cartProductIds = {};
    $$('[data-bl-cart-product-id]', freshHost).forEach(function (row) {
      cartProductIds[row.getAttribute('data-bl-cart-product-id')] = true;
    });

    var visibleCards = 0;
    $$('[data-bl-upsell-product-id]', freshUpsell).forEach(function (card) {
      var inCart = !!cartProductIds[card.getAttribute('data-bl-upsell-product-id')];
      card.hidden = inCart;
      card.setAttribute('data-bl-upsell-in-cart', inCart ? 'true' : 'false');
      if (!inCart) visibleCards += 1;
    });
    freshUpsell.setAttribute('data-bl-upsell-visible-count', String(visibleCards));
    freshUpsell.hidden = visibleCards === 0;
  }

  function visibleUpsellCards(upsell) {
    return $$('[data-bl-upsell-product-id]', upsell).filter(function (card) {
      return !card.hidden && card.getAttribute('data-bl-upsell-in-cart') !== 'true';
    });
  }

  function updateUpsellControls(upsell, index) {
    var cards = visibleUpsellCards(upsell);
    var lastIndex = Math.max(0, cards.length - 1);
    index = Math.max(0, Math.min(index, lastIndex));
    upsell.setAttribute('data-bl-upsell-index', String(index));

    var previous = $('[data-bl-upsell-prev]', upsell);
    var next = $('[data-bl-upsell-next]', upsell);
    if (previous) previous.disabled = cards.length <= 1 || index === 0;
    if (next) next.disabled = cards.length <= 1 || index === lastIndex;

    $$('[data-bl-upsell-dot]', upsell).forEach(function (dot, dotIndex) {
      var active = dotIndex === index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });
  }

  function goToUpsellSlide(upsell, index, animate) {
    var slider = $('[data-bl-upsell-slider]', upsell);
    var cards = visibleUpsellCards(upsell);
    if (!slider || !cards.length) return;
    index = Math.max(0, Math.min(index, cards.length - 1));
    var sliderBox = slider.getBoundingClientRect();
    var cardBox = cards[index].getBoundingClientRect();
    slider.scrollTo({
      left: slider.scrollLeft + cardBox.left - sliderBox.left,
      behavior: animate ? 'smooth' : 'auto'
    });
    updateUpsellControls(upsell, index);
  }

  function initUpsellSliders(scope) {
    $$('.bl-cart-upsell', scope || document).forEach(function (upsell) {
      var slider = $('[data-bl-upsell-slider]', upsell);
      var pagination = $('[data-bl-upsell-pagination]', upsell);
      var cards = visibleUpsellCards(upsell);
      if (!slider || !pagination) return;

      pagination.innerHTML = '';
      cards.forEach(function (_card, index) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'bl-cart-upsell__pagination-dot';
        dot.setAttribute('data-bl-upsell-dot', String(index));
        dot.setAttribute('aria-label', 'Show upsell product ' + (index + 1));
        pagination.appendChild(dot);
      });

      if (!slider._blUpsellBound) {
        slider._blUpsellBound = true;
        var frame = null;
        slider.addEventListener('scroll', function () {
          if (frame) window.cancelAnimationFrame(frame);
          frame = window.requestAnimationFrame(function () {
            var width = slider.clientWidth || 1;
            updateUpsellControls(upsell, Math.round(slider.scrollLeft / width));
          });
        }, { passive: true });
      }

      var initialIndex = Number(upsell.getAttribute('data-bl-upsell-index')) || 0;
      updateUpsellControls(upsell, initialIndex);
      window.requestAnimationFrame(function () {
        goToUpsellSlide(upsell, initialIndex, false);
      });
    });
  }

  on('click', '[data-bl-upsell-prev]', function (e, button) {
    e.preventDefault();
    var upsell = button.closest('.bl-cart-upsell');
    if (!upsell) return;
    goToUpsellSlide(upsell, (Number(upsell.getAttribute('data-bl-upsell-index')) || 0) - 1, true);
  });

  on('click', '[data-bl-upsell-next]', function (e, button) {
    e.preventDefault();
    var upsell = button.closest('.bl-cart-upsell');
    if (!upsell) return;
    goToUpsellSlide(upsell, (Number(upsell.getAttribute('data-bl-upsell-index')) || 0) + 1, true);
  });

  on('click', '[data-bl-upsell-dot]', function (e, dot) {
    e.preventDefault();
    var upsell = dot.closest('.bl-cart-upsell');
    if (!upsell) return;
    goToUpsellSlide(upsell, Number(dot.getAttribute('data-bl-upsell-dot')) || 0, true);
  });

  window.BerlindeUpsell = { init: initUpsellSliders };

  function refreshCart() {
    var hadFocusInside = false;
    var host = $('[data-bl-cart-host]');
    var sectionId = cartSectionId();
    if (host && document.activeElement && host.contains(document.activeElement)) hadFocusInside = true;

    return fetch(sectionsUrl(sectionId))
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (data) {
        if (!data) return;
        var sectionHtml = data[sectionId] || data[CART_SECTION];
        if (!sectionHtml) return;
        var target = $('[data-bl-cart-host]');
        if (!target) return;
        var wasOpen = isCartOpen();
        var parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
        var fresh = parsed.querySelector('[data-bl-cart-host]');
        if (!fresh) return;

        preserveAndSyncUpsell(target, fresh);
        target.innerHTML = fresh.innerHTML;
        initUpsellSliders(target);

        if (wasOpen) {
          var cart = $('[data-bl-cart]');
          if (cart) {
            cart.dataset.open = 'true';
            cart.setAttribute('aria-hidden', 'false');
          }
          showScrim(true);
          // The node the user was on has just been destroyed.
          if (hadFocusInside) {
            var close = $('[data-bl-cart-close]');
            if (close) close.focus();
          }
        }
        syncCartCount();
      })
      .catch(function () { /* keep the last good drawer rather than blanking it */ });
  }

  function syncCartCount() {
    var source = $('[data-bl-cart-count-source]');
    if (!source) return;
    var value = source.textContent.trim();
    $$('[data-bl-cart-count]').forEach(function (el) { el.textContent = value; });
  }

  function cartRequest(url, body) {
    setBusy(true);
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (res) {
        return res.json().then(function (json) {
          if (!res.ok) throw new Error((json && json.description) || 'cart');
          return json;
        });
      })
      .then(function () { return refreshCart(); })
      .finally(function () { setBusy(false); });
  }

  on('click', '[data-bl-cart-open]', function (e) { e.preventDefault(); openCart(); });
  on('click', '[data-bl-cart-close]', function (e) { e.preventDefault(); closeCart(); });
  on('click', '[data-bl-scrim]', function (e) { e.preventDefault(); closeCart(); closeNav(); });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    closeCart();
    closeNav();
  });

  on('click', '[data-bl-qty]', function (e, el) {
    e.preventDefault();
    if (cartBusy) return;
    var line = parseInt(el.getAttribute('data-bl-line'), 10);
    var next = parseInt(el.getAttribute('data-bl-qty'), 10);
    if (!line || isNaN(next)) return;
    cartRequest('/cart/change.js', { line: line, quantity: Math.max(0, next) })
      .catch(function () {
        // Stock ran out or the line moved; the server view is the truth.
        refreshCart();
      });
  });

  /* --- Add to cart -------------------------------------------------------- */

  on('submit', 'form[data-bl-add]', function (e, form) {
    e.preventDefault();
    if (cartBusy) return;

    var button = form.querySelector('[data-bl-atc]');
    var idField = form.querySelector('[name="id"]');
    if (!idField || !idField.value) return;
    if (button && button.getAttribute('aria-disabled') === 'true') return;

    var qtyField = form.querySelector('[name="quantity"]');
    var error = form.querySelector('[data-bl-add-error]');
    var original = button ? button.textContent : '';

    if (error) error.hidden = true;
    if (button) {
      button.setAttribute('aria-disabled', 'true');
      button.dataset.blPending = 'true';
      var busyLabel = button.getAttribute('data-bl-adding');
      if (busyLabel) button.textContent = busyLabel;
    }

    cartRequest('/cart/add.js', {
      items: [{ id: Number(idField.value), quantity: qtyField ? Number(qtyField.value) || 1 : 1 }]
    })
      .then(function () { openCart(); })
      .catch(function () { if (error) error.hidden = false; })
      .finally(function () {
        if (!button) return;
        delete button.dataset.blPending;
        // The shopper may have switched to a sold-out variant while this was in
        // flight; in that case the picker owns the button, not this handler.
        if (button.dataset.blUnavailable === 'true') return;
        button.removeAttribute('aria-disabled');
        button.textContent = original;
      });
  });

  /* --- Mobile navigation --------------------------------------------------- */

  function closeNav() {
    var nav = $('[data-bl-nav]');
    if (!nav || nav.dataset.open !== 'true') return;
    nav.dataset.open = 'false';
    nav.setAttribute('aria-hidden', 'true');
    if (!isCartOpen()) showScrim(false);
    lockScroll(false);
    var trigger = $('[data-bl-nav-open]');
    if (trigger) trigger.focus();
  }

  on('click', '[data-bl-nav-open]', function (e) {
    e.preventDefault();
    var nav = $('[data-bl-nav]');
    if (!nav || nav.dataset.open === 'true') return;
    nav.dataset.open = 'true';
    nav.setAttribute('aria-hidden', 'false');
    showScrim(true);
    lockScroll(true);
    var close = nav.querySelector('[data-bl-nav-close]');
    if (close) close.focus();
  });
  on('click', '[data-bl-nav-close]', function (e) { e.preventDefault(); closeNav(); });

  /* --- Announcement dismissal ---------------------------------------------- */

  var ANN_KEY = 'berlinde:announcement';

  function initAnnouncement() {
    var bar = $('[data-bl-announcement]');
    if (!bar) return;
    var stamp = bar.getAttribute('data-bl-announcement');
    try {
      if (stamp && window.localStorage.getItem(ANN_KEY) === stamp) bar.hidden = true;
    } catch (err) { /* private browsing: just show it */ }
  }

  on('click', '[data-bl-announcement-close]', function (e) {
    e.preventDefault();
    var bar = $('[data-bl-announcement]');
    if (!bar) return;
    bar.hidden = true;
    try {
      window.localStorage.setItem(ANN_KEY, bar.getAttribute('data-bl-announcement') || '1');
    } catch (err) { /* nothing to do */ }
  });

  /* --- Sticky header shrink ------------------------------------------------ */

  var headerBound = false;

  function applyHeaderShrink() {
    var header = $('[data-bl-header]');
    if (!header || !header.classList.contains('bl-header--sticky')) return;
    header.classList.toggle('bl-header--shrunk', scrollTop() > 200);
  }

  function initHeaderShrink() {
    if (!headerBound) {
      headerBound = true;
      var ticking = false;
      onScroll(function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          applyHeaderShrink();
          ticking = false;
        });
      });
    }
    applyHeaderShrink();
  }

  /* --- Collection filters -------------------------------------------------- */

  // Checkboxes and the sort select submit Shopify's own filter form. Number
  // inputs are left alone so a shopper can finish typing a price.
  on('change', '[data-bl-filter-form] input[type="checkbox"], [data-bl-filter-form] select', function (e, el) {
    var form = el.closest('[data-bl-filter-form]');
    if (form) form.submit();
  });

  // Only one filter dropdown open at a time.
  document.addEventListener('click', function (e) {
    $$('[data-bl-filter][open]').forEach(function (details) {
      if (!details.contains(e.target)) details.removeAttribute('open');
    });
  });

  /* --- Product variants ----------------------------------------------------- */

  function initVariants() {
    $$('[data-bl-product]').forEach(function (scope) {
      if (scope.dataset.blBound === 'true') return;
      var dataEl = scope.querySelector('[data-bl-variants]');
      if (!dataEl) return;

      var variants;
      try { variants = JSON.parse(dataEl.textContent); } catch (err) { return; }
      scope.dataset.blBound = 'true';

      function selected() {
        return $$('[data-bl-option]', scope)
          .filter(function (input) { return input.checked; })
          .sort(function (a, b) { return Number(a.dataset.blOptionIndex) - Number(b.dataset.blOptionIndex); })
          .map(function (input) { return input.value; });
      }

      function update() {
        var chosen = selected();

        $$('[data-bl-option-display]', scope).forEach(function (out) {
          var index = Number(out.getAttribute('data-bl-option-display'));
          out.textContent = chosen[index] || '';
        });

        var match = variants.find(function (v) {
          return v.options.length === chosen.length && v.options.every(function (o, i) { return o === chosen[i]; });
        });

        var idField = scope.querySelector('[data-bl-variant-id]');
        var button = scope.querySelector('[data-bl-atc]');

        function markUnavailable(labelAttr) {
          if (!button) return;
          button.dataset.blUnavailable = 'true';
          button.setAttribute('aria-disabled', 'true');
          var label = button.getAttribute(labelAttr);
          if (label) button.textContent = label;
        }

        if (!match) {
          if (idField) idField.value = '';
          markUnavailable('data-bl-unavailable');
          return;
        }

        if (idField) idField.value = match.id;

        if (button) {
          if (match.available) {
            delete button.dataset.blUnavailable;
            // Do not steal the button back while an add is still in flight.
            if (button.dataset.blPending !== 'true') {
              button.removeAttribute('aria-disabled');
              var ok = button.getAttribute('data-bl-available');
              if (ok) button.textContent = ok;
            }
          } else {
            markUnavailable('data-bl-soldout');
          }
        }

        $$('[data-bl-price-for]', scope).forEach(function (node) {
          node.hidden = node.getAttribute('data-bl-price-for') !== String(match.id);
        });

        if (match.media_id) {
          var gallery = scope.querySelector('[data-bl-gallery]');
          var target = scope.querySelector('[data-bl-media="' + match.media_id + '"]');
          if (gallery && target && target.parentElement === gallery) gallery.prepend(target);
        }

        if (window.history && window.history.replaceState) {
          try {
            var url = new URL(window.location.href);
            url.searchParams.set('variant', match.id);
            window.history.replaceState({}, '', url.toString());
          } catch (err) { /* nothing to do */ }
        }
      }

      scope.addEventListener('change', function (e) {
        if (e.target instanceof Element && e.target.hasAttribute('data-bl-option')) update();
      });
      update();
    });
  }

  /* --- Boot ----------------------------------------------------------------- */

  function boot() {
    initAnnouncement();
    initHeaderShrink();
    initVariants();
    initUpsellSliders(document);
    syncCartCount();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function () {
    releaseAllLocks();
    boot();
  });
  document.addEventListener('shopify:section:select', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('[data-bl-cart]')) openCart();
  });
})();
